import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Select, InputRef } from "antd";
import { Countries as CountryList } from "./country.data";
import { FlagIcon } from "./flag-icon.component";
import "./phone-number.css";

const DEFAULT_COUNTRY = "us";

export interface PhoneNumberProps {
  value?: string;
  defaultCountryCode?: string;
  onChange: (value: string) => void;
  onEnterKeyPress?: () => void;
  autoFocus?: boolean;
  className?: string;
}

type Country = (typeof CountryList)[number];

const parseE164 = (v?: string) => {
  const str = (v || "").trim();
  if (!str.startsWith("+")) return { dialCode: "", national: "" };
  const sorted = [...CountryList].sort(
    (a, b) => b.dialCode.length - a.dialCode.length
  );
  const match = sorted.find((c) => str.startsWith(c.dialCode));
  if (!match) return { dialCode: "", national: str.replace(/\D/g, "") };
  const national = str.slice(match.dialCode.length).replace(/\D/g, "");
  return { dialCode: match.dialCode, national, code: match.code };
};

export const PhoneNumber: React.FC<PhoneNumberProps> = ({
  value,
  defaultCountryCode = DEFAULT_COUNTRY,
  onChange,
  onEnterKeyPress = () => {},
  autoFocus = false,
  className = "",
}) => {
  const phoneNumberInput = useRef<InputRef>(null);
  const selectRef = useRef<any>(null);

  const initial = useMemo(() => parseE164(value), [value]);

  const [codeValue, setCodeValue] = useState<string>(
    initial.code || defaultCountryCode
  );
  const [phoneNumberValue, setPhoneNumberValue] = useState<string>(
    initial.national || ""
  );
  const [open, setOpen] = useState(false);
  const [countries, setCountries] = useState(CountryList);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (typeof value === "string") {
      const parsed = parseE164(value);
      if (parsed.code && parsed.code !== codeValue) setCodeValue(parsed.code);
      if (parsed.national !== phoneNumberValue)
        setPhoneNumberValue(parsed.national || "");
    }
  }, [value]);

  useEffect(() => {
    if (autoFocus) phoneNumberInput.current?.focus?.();
  }, [autoFocus]);

  const byCode = useCallback(
    (code: string): Country | undefined =>
      CountryList.find((c) => c.code === code),
    []
  );
  const dialCodeOf = useCallback(
    (code: string) => byCode(code)?.dialCode || "",
    [byCode]
  );

  const emit = useCallback(
    (code: string, num: string) => {
      const dial = dialCodeOf(code);
      onChange(num ? `${dial}${num}` : dial || "");
    },
    [dialCodeOf, onChange]
  );

  const resetSearch = useCallback(() => {
    setSearchText("");
    setCountries(CountryList);
  }, []);

  const handleSearch = useCallback((v: string) => {
    setSearchText(v);
    if (!v) {
      setCountries(CountryList);
      return;
    }
    const q = v.trim().toLowerCase();
    const filtered = CountryList.filter(
      (country) =>
        country.code.toLowerCase().includes(q) ||
        country.name.toLowerCase().includes(q) ||
        country.dialCode.replace("+", "").includes(q.replace("+", ""))
    );
    setCountries(filtered);
  }, []);

  useEffect(() => {
    if (!open || !searchText) return;
    const q = searchText.trim().toLowerCase();
    const pick =
      CountryList.find(
        (c) =>
          c.dialCode.replace("+", "") === q || c.dialCode.toLowerCase() === q
      ) ||
      CountryList.find((c) => c.code.toLowerCase() === q) ||
      CountryList.find((c) => c.name.toLowerCase().startsWith(q));
    if (pick) {
      setCodeValue(pick.code);
      emit(pick.code, phoneNumberValue);
    }
  }, [open, searchText, emit, phoneNumberValue]);

  return (
    <div className={`pn-field mt-2 ${className}`}>
      <button
        type="button"
        className="pn-flag-slot"
        tabIndex={0}
        aria-label="Select country"
        onClick={() => {
          const willOpen = !open;
          setOpen(willOpen);
          if (willOpen) {
            resetSearch();
            requestAnimationFrame(() => selectRef.current?.focus?.());
          }
        }}
      >
        <span className="pn-flag">
          <FlagIcon code={codeValue} />
        </span>
        <span className="pn-dial">{dialCodeOf(codeValue)}</span>
        <span className="pn-chevron">▼</span>
      </button>

      <Select
        ref={selectRef}
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (isOpen) {
            resetSearch();
            requestAnimationFrame(() => selectRef.current?.focus?.());
          }
        }}
        showSearch
        searchValue={searchText}
        onSearch={handleSearch}
        filterOption={false}
        value={codeValue}
        onChange={(newCode) => {
          setCodeValue(newCode);
          emit(newCode, phoneNumberValue);
          if (autoFocus)
            setTimeout(() => phoneNumberInput.current?.focus?.(), 0);
          setOpen(false);
        }}
        options={countries.map((item) => ({
          value: item.code,
          label: (
            <div className="flex items-center">
              <FlagIcon code={item.code} />
              <span className="ml-2 opacity-70">{item.dialCode}</span>
              <span className="ml-2">{item.name}</span>
            </div>
          ),
          key: item.code,
        }))}
        popupClassName="phone-number--dropdown"
        className={`pn-select-overlay ${open ? "pn-select-overlay--open" : ""}`}
        tabIndex={-1}
      />

      <input
        ref={phoneNumberInput as any}
        type="tel"
        className="pn-input"
        pattern="\d*"
        maxLength={15}
        value={phoneNumberValue}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, "");
          setPhoneNumberValue(digits);
          emit(codeValue, digits);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") onEnterKeyPress();
        }}
        placeholder="Enter phone number"
        autoFocus={autoFocus}
      />
    </div>
  );
};
